import React, { useState } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import './TaxConfigurationPage.css';

const GET_TAX_CONFIGURATIONS = gql`
  query GetTaxConfigurations($includeInactive: Boolean) {
    taxConfigurations(includeInactive: $includeInactive) {
      id
      name
      taxRate
      isActive
      appliesToInternal
      appliesToPrincipal
      calculationOrder
      description
      createdAt
      updatedAt
    }
  }
`;

const GET_DAILY_TARGET_SUMMARY = gql`
  query GetDailyTargetSummary($date: DateTime!) {
    dailyTargetSummary(date: $date) {
      targets {
        taxConfiguration {
          id
          name
          taxRate
        }
        target {
          id
          targetAmount
          expectedTaxAmount
          actualTaxAmount
          variance
          variancePercentage
          date
        }
        status
      }
    }
  }
`;

const CREATE_TAX_CONFIGURATION = gql`
  mutation CreateTaxConfiguration($input: CreateTaxConfigurationInput!) {
    createTaxConfiguration(input: $input) {
      id
      name
      taxRate
      isActive
    }
  }
`;

const UPDATE_TAX_CONFIGURATION = gql`
  mutation UpdateTaxConfiguration($id: ID!, $input: UpdateTaxConfigurationInput!) {
    updateTaxConfiguration(id: $id, input: $input) {
      id
      name
      taxRate
      isActive
    }
  }
`;

const DELETE_TAX_CONFIGURATION = gql`
  mutation DeleteTaxConfiguration($id: ID!) {
    deleteTaxConfiguration(id: $id)
  }
`;

const CREATE_DAILY_TAX_TARGET = gql`
  mutation CreateDailyTaxTarget($input: CreateDailyTaxTargetInput!) {
    createDailyTaxTarget(input: $input) {
      id
      targetAmount
      expectedTaxAmount
      date
    }
  }
`;

const UPDATE_DAILY_TAX_TARGET = gql`
  mutation UpdateDailyTaxTarget($id: ID!, $input: UpdateDailyTaxTargetInput!) {
    updateDailyTaxTarget(id: $id, input: $input) {
      id
      actualTaxAmount
      variance
      variancePercentage
    }
  }
`;

interface TaxConfiguration {
  id: string;
  name: string;
  taxRate: number;
  isActive: boolean;
  appliesToInternal: boolean;
  appliesToPrincipal: boolean;
  calculationOrder: number;
  description?: string;
}

export const TaxConfigurationPage: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [showInactive, setShowInactive] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [editingConfig, setEditingConfig] = useState<TaxConfiguration | null>(null);
  const [isEditingTarget, setIsEditingTarget] = useState<string | null>(null);

  const { data: configData, loading: configLoading, refetch: refetchConfigs } = useQuery(
    GET_TAX_CONFIGURATIONS,
    {
      variables: { includeInactive: showInactive },
    }
  );

  const { data: summaryData, loading: summaryLoading, refetch: refetchSummary } = useQuery(
    GET_DAILY_TARGET_SUMMARY,
    {
      variables: { date: selectedDate },
    }
  );

  const [createTaxConfig] = useMutation(CREATE_TAX_CONFIGURATION);
  const [updateTaxConfig] = useMutation(UPDATE_TAX_CONFIGURATION);
  const [deleteTaxConfig] = useMutation(DELETE_TAX_CONFIGURATION);
  const [createDailyTarget] = useMutation(CREATE_DAILY_TAX_TARGET);
  const [updateDailyTarget] = useMutation(UPDATE_DAILY_TAX_TARGET);

  const [formData, setFormData] = useState({
    name: '',
    taxRate: 0,
    appliesToInternal: true,
    appliesToPrincipal: true,
    calculationOrder: 1,
    description: '',
  });

  const [targetFormData, setTargetFormData] = useState({
    targetAmount: 0,
    expectedTaxAmount: 0,
  });

  const handleCreateConfig = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createTaxConfig({
        variables: {
          input: formData,
        },
      });
      refetchConfigs();
      setIsCreating(false);
      setFormData({
        name: '',
        taxRate: 0,
        appliesToInternal: true,
        appliesToPrincipal: true,
        calculationOrder: 1,
        description: '',
      });
    } catch (error) {
      console.error('Error creating tax configuration:', error);
      alert('Error al crear configuración de impuesto');
    }
  };

  const handleUpdateConfig = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingConfig) return;

    try {
      await updateTaxConfig({
        variables: {
          id: editingConfig.id,
          input: formData,
        },
      });
      refetchConfigs();
      setEditingConfig(null);
      setFormData({
        name: '',
        taxRate: 0,
        appliesToInternal: true,
        appliesToPrincipal: true,
        calculationOrder: 1,
        description: '',
      });
    } catch (error) {
      console.error('Error updating tax configuration:', error);
      alert('Error al actualizar configuración de impuesto');
    }
  };

  const handleDeleteConfig = async (id: string) => {
    if (!confirm('¿Está seguro de desactivar esta configuración?')) return;

    try {
      await deleteTaxConfig({ variables: { id } });
      refetchConfigs();
    } catch (error) {
      console.error('Error deleting tax configuration:', error);
      alert('Error al desactivar configuración de impuesto');
    }
  };

  const handleEditConfig = (config: TaxConfiguration) => {
    setEditingConfig(config);
    setFormData({
      name: config.name,
      taxRate: config.taxRate,
      appliesToInternal: config.appliesToInternal,
      appliesToPrincipal: config.appliesToPrincipal,
      calculationOrder: config.calculationOrder,
      description: config.description || '',
    });
    setIsCreating(true);
  };

  const handleCreateTarget = async (taxConfigId: string) => {
    try {
      await createDailyTarget({
        variables: {
          input: {
            taxConfigurationId: taxConfigId,
            date: selectedDate,
            targetAmount: targetFormData.targetAmount,
            expectedTaxAmount: targetFormData.expectedTaxAmount,
          },
        },
      });
      refetchSummary();
      setIsEditingTarget(null);
      setTargetFormData({ targetAmount: 0, expectedTaxAmount: 0 });
    } catch (error) {
      console.error('Error creating daily target:', error);
      alert('Error al crear meta diaria');
    }
  };

  const handleUpdateTarget = async (targetId: string, actualAmount: number) => {
    try {
      await updateDailyTarget({
        variables: {
          id: targetId,
          input: {
            actualTaxAmount: actualAmount,
          },
        },
      });
      refetchSummary();
    } catch (error) {
      console.error('Error updating daily target:', error);
      alert('Error al actualizar meta diaria');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'on-track':
        return '#10b981';
      case 'exceeded':
        return '#3b82f6';
      case 'behind':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'on-track':
        return 'En objetivo';
      case 'exceeded':
        return 'Superado';
      case 'behind':
        return 'Por debajo';
      default:
        return 'Sin meta';
    }
  };

  return (
    <div className="tax-configuration-page">
      <div className="page-header">
        <h1>Configuración de Impuestos</h1>
        <p>Gestiona las configuraciones de impuestos y sus metas diarias</p>
      </div>

      <div className="tax-config-container">
        {/* Left Panel - Tax Configurations */}
        <div className="tax-config-panel">
          <div className="panel-header">
            <h2>Configuraciones de Impuestos</h2>
            <div className="panel-actions">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                />
                <span>Mostrar inactivos</span>
              </label>
              <button
                className="btn-primary"
                onClick={() => {
                  setIsCreating(true);
                  setEditingConfig(null);
                  setFormData({
                    name: '',
                    taxRate: 0,
                    appliesToInternal: true,
                    appliesToPrincipal: true,
                    calculationOrder: 1,
                    description: '',
                  });
                }}
              >
                + Nuevo Impuesto
              </button>
            </div>
          </div>

          {configLoading ? (
            <div className="loading">Cargando configuraciones...</div>
          ) : (
            <div className="config-list">
              {configData?.taxConfigurations?.map((config: TaxConfiguration) => (
                <div
                  key={config.id}
                  className={`config-card ${!config.isActive ? 'inactive' : ''}`}
                >
                  <div className="config-header">
                    <h3>{config.name}</h3>
                    <span className="tax-rate">{config.taxRate}%</span>
                  </div>
                  <div className="config-details">
                    <div className="config-info">
                      <span>Orden: {config.calculationOrder}</span>
                      <span>
                        {config.appliesToInternal && 'Interno'}{' '}
                        {config.appliesToPrincipal && 'Principal'}
                      </span>
                    </div>
                    {config.description && (
                      <p className="config-description">{config.description}</p>
                    )}
                  </div>
                  <div className="config-actions">
                    <button
                      className="btn-edit"
                      onClick={() => handleEditConfig(config)}
                    >
                      Editar
                    </button>
                    {config.isActive && (
                      <button
                        className="btn-delete"
                        onClick={() => handleDeleteConfig(config.id)}
                      >
                        Desactivar
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right Panel - Daily Targets */}
        <div className="tax-targets-panel">
          <div className="panel-header">
            <h2>Metas Diarias</h2>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="date-input"
            />
          </div>

          {summaryLoading ? (
            <div className="loading">Cargando metas...</div>
          ) : (
            <div className="targets-list">
              {summaryData?.dailyTargetSummary?.targets?.map((item: any) => (
                <div key={item.taxConfiguration.id} className="target-card">
                  <div className="target-header">
                    <div>
                      <h3>{item.taxConfiguration.name}</h3>
                      <span className="tax-rate-badge">
                        {item.taxConfiguration.taxRate}%
                      </span>
                    </div>
                    <div
                      className="status-badge"
                      style={{ backgroundColor: getStatusColor(item.status) }}
                    >
                      {getStatusText(item.status)}
                    </div>
                  </div>

                  {item.target ? (
                    <div className="target-details">
                      <div className="target-row">
                        <span>Meta del día:</span>
                        <strong>${item.target.targetAmount.toLocaleString()}</strong>
                      </div>
                      <div className="target-row">
                        <span>Impuesto esperado:</span>
                        <strong>${item.target.expectedTaxAmount.toLocaleString()}</strong>
                      </div>
                      <div className="target-row">
                        <span>Impuesto real:</span>
                        <strong>${item.target.actualTaxAmount.toLocaleString()}</strong>
                      </div>
                      <div className="target-row">
                        <span>Varianza:</span>
                        <strong
                          style={{
                            color: item.target.variance >= 0 ? '#10b981' : '#ef4444',
                          }}
                        >
                          ${item.target.variance.toLocaleString()} (
                          {item.target.variancePercentage.toFixed(2)}%)
                        </strong>
                      </div>
                      <button
                        className="btn-secondary"
                        onClick={() => {
                          const newAmount = prompt(
                            'Ingrese el monto real de impuesto recaudado:',
                            item.target.actualTaxAmount.toString()
                          );
                          if (newAmount) {
                            handleUpdateTarget(item.target.id, parseFloat(newAmount));
                          }
                        }}
                      >
                        Actualizar Monto Real
                      </button>
                    </div>
                  ) : (
                    <div className="no-target">
                      {isEditingTarget === item.taxConfiguration.id ? (
                        <div className="target-form">
                          <input
                            type="number"
                            placeholder="Meta del día"
                            value={targetFormData.targetAmount}
                            onChange={(e) =>
                              setTargetFormData({
                                ...targetFormData,
                                targetAmount: parseFloat(e.target.value) || 0,
                              })
                            }
                          />
                          <input
                            type="number"
                            placeholder="Impuesto esperado"
                            value={targetFormData.expectedTaxAmount}
                            onChange={(e) =>
                              setTargetFormData({
                                ...targetFormData,
                                expectedTaxAmount: parseFloat(e.target.value) || 0,
                              })
                            }
                          />
                          <div className="form-actions">
                            <button
                              className="btn-primary"
                              onClick={() => handleCreateTarget(item.taxConfiguration.id)}
                            >
                              Crear
                            </button>
                            <button
                              className="btn-secondary"
                              onClick={() => setIsEditingTarget(null)}
                            >
                              Cancelar
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <p>No hay meta configurada para este día</p>
                          <button
                            className="btn-primary"
                            onClick={() => setIsEditingTarget(item.taxConfiguration.id)}
                          >
                            + Crear Meta
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modal for Create/Edit */}
      {isCreating && (
        <div className="modal-overlay" onClick={() => setIsCreating(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingConfig ? 'Editar' : 'Crear'} Configuración de Impuesto</h2>
              <button className="close-btn" onClick={() => setIsCreating(false)}>
                ×
              </button>
            </div>
            <form onSubmit={editingConfig ? handleUpdateConfig : handleCreateConfig}>
              <div className="form-group">
                <label>Nombre del Impuesto</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ej: IVA, Consumo, etc."
                />
              </div>

              <div className="form-group">
                <label>Tasa de Impuesto (%)</label>
                <input
                  type="number"
                  required
                  step="0.01"
                  value={formData.taxRate}
                  onChange={(e) =>
                    setFormData({ ...formData, taxRate: parseFloat(e.target.value) || 0 })
                  }
                  placeholder="Ej: 19"
                />
              </div>

              <div className="form-group">
                <label>Orden de Cálculo</label>
                <input
                  type="number"
                  required
                  value={formData.calculationOrder}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      calculationOrder: parseInt(e.target.value) || 1,
                    })
                  }
                />
              </div>

              <div className="form-row">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.appliesToInternal}
                    onChange={(e) =>
                      setFormData({ ...formData, appliesToInternal: e.target.checked })
                    }
                  />
                  <span>Aplica a facturas internas</span>
                </label>

                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.appliesToPrincipal}
                    onChange={(e) =>
                      setFormData({ ...formData, appliesToPrincipal: e.target.checked })
                    }
                  />
                  <span>Aplica a facturas principales</span>
                </label>
              </div>

              <div className="form-group">
                <label>Descripción (Opcional)</label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="Descripción del impuesto"
                  rows={3}
                />
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-primary">
                  {editingConfig ? 'Actualizar' : 'Crear'}
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setIsCreating(false)}
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
